import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { TableColumn } from "@/components/table/data-table";

export interface SortState {
	column: string;
	direction: "asc" | "desc";
	priority: number;
}

interface SortingState {
	currentSorts: SortState[];
	setSort: (column: string, direction: "asc" | "desc") => void;
	toggleSort: (column: string, isMultiSort?: boolean) => void;
	addSort: (column: string, direction: "asc" | "desc") => void;
	removeSort: (column: string) => void;
	clearSort: () => void;
	resetToDefault: () => void;
	getSortedData: <T>(data: T[]) => T[];
	isSorted: (column: string) => boolean;
	getSortDirection: (column: string) => "asc" | "desc" | null;
	getSortPriority: (column: string) => number | null;
	isMultiSortActive: boolean;
}

export function useSorting(
	tableId: string,
	columns: TableColumn[],
	defaultSorts?: SortState[],
	enableMultiSort: boolean = true,
): SortingState {
	const storageKey = useMemo(() => `table-sort-${tableId}`, [tableId]);

	const defaultSortStates = useMemo(() => defaultSorts || [], [defaultSorts]);

	const sortableColumns = useMemo(
		() =>
			new Set(
				columns
					.filter((col) => col.sortable !== false)
					.map((col) => col.key),
			),
		[columns],
	);

	const persistSorts = useCallback(
		(newSorts: SortState[]) => {
			try {
				localStorage.setItem(storageKey, JSON.stringify(newSorts));
			} catch (error) {
				console.warn("Error saving sorts to localStorage:", error);
			}
		},
		[storageKey],
	);

	// Estado inicial
	const getInitialSorts = useCallback((): SortState[] => {
		try {
			const stored = localStorage.getItem(storageKey);
			if (stored) {
				const parsed = JSON.parse(stored) as SortState[];

				if (!Array.isArray(parsed)) {
					return defaultSortStates;
				}

				const validSorts = parsed.filter(
					(sort) => sort.column && sortableColumns.has(sort.column),
				);

				if (validSorts.length > 0) {
					// console.log("📊 Restored sorts from localStorage:", validSorts);
					return validSorts;
				}
			}
		} catch (error) {
			console.warn("Error loading sorts from localStorage:", error);
		}

		return defaultSortStates;
	}, [storageKey, sortableColumns, defaultSortStates]);

	const [currentSorts, setCurrentSorts] =
		useState<SortState[]>(getInitialSorts);

	const prevSortsRef = useRef<string>("");

	useEffect(() => {
		const currentSortsStr = JSON.stringify(currentSorts);

		// Solo persistir si realmente cambió
		if (prevSortsRef.current !== currentSortsStr) {
			prevSortsRef.current = currentSortsStr;
			persistSorts(currentSorts);
		}
	}, [currentSorts, persistSorts]);

	// Actualizar cuando cambien las columnas
	useEffect(() => {
		setCurrentSorts(getInitialSorts());
	}, [getInitialSorts]);

	const setSort = useCallback(
		(column: string, direction: "asc" | "desc") => {
			if (!sortableColumns.has(column)) {
				console.warn("⚠️ Column is not sortable:", column);
				return;
			}

			const newSorts: SortState[] = [{ column, direction, priority: 0 }];
			setCurrentSorts(newSorts);
			// console.log("🔄 Sort set:", newSorts);
		},
		[sortableColumns],
	);

	const addSort = useCallback(
		(column: string, direction: "asc" | "desc") => {
			if (!enableMultiSort) {
				setSort(column, direction);
				return;
			}

			if (!sortableColumns.has(column)) {
				console.warn("⚠️ Column is not sortable:", column);
				return;
			}

			setCurrentSorts((prev) => {
				const existingIndex = prev.findIndex((s) => s.column === column);

				if (existingIndex >= 0) {
					const updated = [...prev];
					updated[existingIndex] = {
						...updated[existingIndex],
						direction,
					};
					return updated;
				}

				const newSorts = [
					...prev,
					{ column, direction, priority: prev.length },
				];
				return newSorts;
			});
		},
		[enableMultiSort, sortableColumns, setSort],
	);

	const removeSort = useCallback(
		(column: string) => {
			setCurrentSorts((prev) => {
				const filtered = prev.filter((s) => s.column !== column);

				// Reajustar prioridades
				const reindexed = filtered.map((sort, index) => ({
					...sort,
					priority: index,
				}));

				const newSorts =
					reindexed.length > 0 ? reindexed : defaultSortStates;
				return newSorts;
			});
		},
		[defaultSortStates],
	);

	const toggleSort = useCallback(
		(column: string, isMultiSort: boolean = false) => {
			if (!sortableColumns.has(column)) {
				console.warn("⚠️ Column is not sortable:", column);
				return;
			}

			if (!enableMultiSort || !isMultiSort) {
				// Sort único
				setCurrentSorts((prev) => {
					const existingSort = prev.find((s) => s.column === column);

					if (existingSort) {
						if (existingSort.direction === "asc") {
							// console.log("🔄 Toggled to DESC:", column);
							return [{ column, direction: "desc", priority: 0 }];
						} else {
							// console.log("🔄 Reset to default sort");
							return defaultSortStates;
						}
					} else {
						// console.log("🔄 New sort column (ASC):", column);
						return [{ column, direction: "asc", priority: 0 }];
					}
				});
				return;
			}

			// Multi-sort
			setCurrentSorts((prev) => {
				const existingIndex = prev.findIndex((s) => s.column === column);

				if (existingIndex >= 0) {
					const existing = prev[existingIndex];
					if (existing.direction === "asc") {
						const updated = [...prev];
						updated[existingIndex] = { ...existing, direction: "desc" };
						// console.log("🔄 Multi-sort toggled to DESC:", column);
						return updated;
					} else {
						const filtered = prev.filter((s) => s.column !== column);
						const reindexed = filtered.map((sort, index) => ({
							...sort,
							priority: index,
						}));
						const newSorts =
							reindexed.length > 0 ? reindexed : defaultSortStates;
						return newSorts;
					}
				}

				const newSorts = [
					...prev,
					{ column, direction: "asc" as const, priority: prev.length },
				];
				return newSorts;
			});
		},
		[enableMultiSort, sortableColumns, defaultSortStates],
	);

	const clearSort = useCallback(() => {
		setCurrentSorts([]);
	}, []);

	const resetToDefault = useCallback(() => {
		setCurrentSorts(defaultSortStates);
	}, [defaultSortStates]);

	const sortsMap = useMemo(() => {
		const map = new Map<string, SortState>();
		currentSorts.forEach((sort) => map.set(sort.column, sort));
		return map;
	}, [currentSorts]);

	const isSorted = useCallback(
		(column: string): boolean => {
			return sortsMap.has(column);
		},
		[sortsMap],
	);

	const getSortDirection = useCallback(
		(column: string): "asc" | "desc" | null => {
			return sortsMap.get(column)?.direction ?? null;
		},
		[sortsMap],
	);

	const getSortPriority = useCallback(
		(column: string): number | null => {
			return sortsMap.get(column)?.priority ?? null;
		},
		[sortsMap],
	);

	const isMultiSortActive = useMemo(
		() => currentSorts.length > 1,
		[currentSorts],
	);

	const getNestedValue = useCallback((obj: any, path: string): any => {
		return path.split(".").reduce((acc, part) => acc?.[part], obj);
	}, []);

	const detectValueType = useCallback(
		(value: any): "number" | "date" | "boolean" | "string" => {
			if (value == null) return "string";
			if (typeof value === "boolean") return "boolean";
			if (typeof value === "number") return "number";
			if (value instanceof Date) return "date";

			if (typeof value === "string") {
				const datePatterns = [
					/^\d{4}-\d{2}-\d{2}/,
					/^\d{2}\/\d{2}\/\d{4}/,
					/^\d{2}-\d{2}-\d{4}/,
				];

				if (datePatterns.some((pattern) => pattern.test(value))) {
					const parsed = new Date(value);
					if (!Number.isNaN(parsed.getTime())) return "date";
				}
			}

			return "string";
		},
		[],
	);

	const compareValues = useCallback(
		(
			a: any,
			b: any,
			column: TableColumn,
			direction: "asc" | "desc",
		): number => {
			const aValue = getNestedValue(a, column.key);
			const bValue = getNestedValue(b, column.key);

			if (aValue == null && bValue == null) return 0;
			if (aValue == null) return 1;
			if (bValue == null) return -1;

			const valueType = detectValueType(aValue);
			let comparison = 0;

			switch (valueType) {
				case "number":
					comparison = Number(aValue) - Number(bValue);
					break;

				case "date": {
					const dateA = new Date(aValue).getTime();
					const dateB = new Date(bValue).getTime();
					comparison = dateA - dateB;
					break;
				}

				case "boolean":
					comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
					break;

				case "string":
				default:
					comparison = String(aValue).localeCompare(
						String(bValue),
						undefined,
						{
							numeric: true,
							sensitivity: "base",
						},
					);
					break;
			}

			return direction === "asc" ? comparison : -comparison;
		},
		[getNestedValue, detectValueType],
	);

	const columnsMap = useMemo(() => {
		const map = new Map<string, TableColumn>();
		columns.forEach((col) => map.set(col.key, col));
		return map;
	}, [columns]);

	const getSortedData = useCallback(
		<T>(data: T[]): T[] => {
			if (currentSorts.length === 0) {
				return data;
			}

			const sortedData = [...data];

			sortedData.sort((a, b) => {
				for (const sortState of currentSorts) {
					const column = columnsMap.get(sortState.column);
					if (!column) {
						console.warn("⚠️ Sort column not found:", sortState.column);
						continue;
					}

					const comparison = compareValues(
						a,
						b,
						column,
						sortState.direction,
					);

					if (comparison !== 0) {
						return comparison;
					}
				}

				return 0;
			});

			return sortedData;
		},
		[currentSorts, columnsMap, compareValues],
	);

	return {
		currentSorts,
		setSort,
		toggleSort,
		addSort,
		removeSort,
		clearSort,
		resetToDefault,
		getSortedData,
		isSorted,
		getSortDirection,
		getSortPriority,
		isMultiSortActive,
	};
}

export default useSorting;
